import {useHarmonicIntervalFn} from 'react-use'

/**
 * @param ref The ref to the component, which exposes a `reset` method.
 * @param onDone A callback function to be called when the timer is done.
 * @param className The class name to be applied to the component.
 * @param seconds The number of seconds to count down from.
 */
const Timer = memo(function Timer({
  onDone,
  className,
  seconds,
  ref,
}: {
  onDone?: () => void
  className?: string
  seconds: number
  ref?: Ref<
    | {
        reset: () => void
      }
    | undefined
  >
}) {
  const [secondsLeft, setSecondsLeft] = useState(seconds)

  const reset = useCallback(() => {
    setSecondsLeft(seconds)
  }, [seconds])

  useImperativeHandle(
    ref,
    () => ({
      reset,
    }),
    [reset],
  )

  const latestOnDone = useLatest(onDone)

  useHarmonicIntervalFn(() => {
    setSecondsLeft(prev => {
      if (prev === 1) latestOnDone.current?.()
      return prev > 0 ? prev - 1 : 0
    })
  }, 1000)

  const minutes = Math.floor(secondsLeft / 60)
  const remainingSeconds = secondsLeft % 60

  return (
    <span className={className}>
      {minutes}:{String(remainingSeconds).padStart(2, '0')}s
    </span>
  )
})

export default Timer
